// bluaIQ — Shared subscription / plan hook
//
// Replaces the previous `demoPlan = 'PRO'` localStorage default that
// drove client-side `ProFeatureGate currentPlan={...}` everywhere. In
// production, plan gating is now server-authoritative: the hook reads
// `GET /api/subscription` (Clerk-authenticated) and returns the user's
// real subscribed plan.
//
// SAFETY POSTURE
//   * Plan defaults to `STARTER` when:
//       - the API call is in flight,
//       - the API call errored,
//       - the API returned a value we cannot normalize, or
//       - no Clerk session is available (anonymous tab, signed-out).
//     We never default to `PRO`.
//   * `localStorage` plan values (`bluaiq_demo_plan`,
//     `constructai_demo_plan`) are only consulted when
//     `isDemoMode()` returns true. They have no effect in production.
//   * `cycleDemoPlan()` is a no-op outside demo mode.
//
// CLERK BOUNDARY
//   `main.tsx` only wraps `<App />` in `<ClerkProvider>` when
//   `VITE_CLERK_PUBLISHABLE_KEY` is set. In trial-mode bundles
//   (no Clerk key), calling `useAuth()` would throw because there is
//   no provider in the tree. To stay compliant with the rules of
//   hooks, we expose two implementations and pick one at module load
//   time — `hasClerk` is computed from `import.meta.env`, which is
//   constant for the lifetime of the bundle. Each call site sees a
//   stable hook identity, so `useAuth()` is either always called or
//   never called for that bundle.
//
// SCOPE
//   This hook surfaces the full ACTIVE/PAUSED/PAST_DUE/INCOMPLETE/
//   CANCELLED enum once the server exposes it. `subscriptionStatus`
//   is read directly from `/api/subscription` (PR #59 added it). If
//   the server response is older or partial we fall back to the
//   `paused` boolean for backward compat, defaulting to ACTIVE.
//   `effectivePlan` prefers the server's `effectivePlan` field (which
//   downgrades CANCELLED/INCOMPLETE users to STARTER) and only falls
//   back to `plan` when the server hasn't sent the new field yet.
//
// NEVER stores the real subscription plan in localStorage as the
// source of truth. React Query owns the cache.

import { apiFetch } from '../utils/api'
import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'

import { isDemoMode } from '../utils/demoData'
import {
  readLegacyMigratedItem,
  writeLegacyMigratedItem,
} from '../utils/legacyBrandStorage'
import { PLAN_KEYS, type PlanKey } from '../shared/plans'

// ── Types ─────────────────────────────────────────────────────────

// Valid plans derive from the shared plan catalog (src/shared/plans.ts) — same
// 'STARTER' | 'PRO' | 'BUILDER' union, preserved via `PLAN_KEYS as const`.
export const VALID_PLANS = PLAN_KEYS
export type Plan = PlanKey

export const VALID_SUBSCRIPTION_STATUSES = [
  'ACTIVE',
  'PAUSED',
  'PAST_DUE',
  'INCOMPLETE',
  'CANCELLED',
] as const
export type SubscriptionStatus = (typeof VALID_SUBSCRIPTION_STATUSES)[number]

export interface SubscriptionPlanState {
  /** The plan returned by the server, or null until the fetch resolves. */
  plan: Plan | null
  /** Best-effort status surfaced from `/api/subscription { paused }`. */
  subscriptionStatus: SubscriptionStatus
  isLoading: boolean
  isError: boolean
  /**
   * True only when the bundle is running in demo mode (Vite dev with no
   * VITE_API_URL — see `isDemoMode()`). Production tenants always see
   * `false` here.
   */
  isDemo: boolean
  /**
   * The plan to feed `<ProFeatureGate currentPlan={...}>` etc.
   *   - In demo mode: the localStorage demo selection (default STARTER).
   *   - In production: server `plan` once loaded; STARTER fallback otherwise.
   * Never returns `'PRO'` or `'BUILDER'` as a default.
   */
  effectivePlan: Plan
  /** Demo-mode plan cycler (STARTER → PRO → BUILDER → STARTER). No-op outside demo mode. */
  cycleDemoPlan: () => void
  /**
   * PR2: 'read_only' when the account has no paid subscription (cancelled,
   * incomplete or trial over). Defaults to 'full' until the server says so —
   * the server is the one enforcing it (402 SUBSCRIPTION_READ_ONLY).
   */
  accessMode?: 'full' | 'read_only'
  accessReason?: string | null
}

/** PR2: pure reader for the server's access block (exported for tests). */
export function readAccessMode(data: { access?: { mode?: unknown; reason?: unknown } } | null | undefined): {
  accessMode: 'full' | 'read_only'
  accessReason: string | null
} {
  const mode = data?.access?.mode === 'read_only' ? 'read_only' : 'full'
  const reason = typeof data?.access?.reason === 'string' ? data.access.reason : null
  return { accessMode: mode, accessReason: reason }
}

// ── Pure helpers (exported for unit tests) ────────────────────────

/**
 * Coerce arbitrary input to a known plan. Anything we don't recognize
 * — including `null`, `undefined`, empty strings, or unexpected
 * casing on the server — falls back to STARTER. NEVER to PRO.
 */
export function normalizePlan(raw: unknown): Plan {
  if (typeof raw !== 'string') return 'STARTER'
  const upper = raw.toUpperCase()
  return (VALID_PLANS as readonly string[]).includes(upper) ? (upper as Plan) : 'STARTER'
}

/**
 * Coerce arbitrary input to a known subscription status. Unknown
 * values fall back to `'ACTIVE'` so a stale/old server response
 * (pre-PR #59) keeps existing behavior. We never invent PAST_DUE or
 * CANCELLED — the server must say so.
 */
export function normalizeSubscriptionStatus(raw: unknown): SubscriptionStatus {
  if (typeof raw !== 'string') return 'ACTIVE'
  const upper = raw.toUpperCase()
  return (VALID_SUBSCRIPTION_STATUSES as readonly string[]).includes(upper)
    ? (upper as SubscriptionStatus)
    : 'ACTIVE'
}

// ── Endpoint contract (subset we consume) ─────────────────────────

interface SubscriptionApiResponse {
  /** STARTER | PRO | BUILDER per `resolvePlan()` on the server. */
  plan?: string
  /** Server-derived plan after cancelled/incomplete downgrade. */
  effectivePlan?: string
  /** Full subscription status (PR #59+). Older servers may omit. */
  subscriptionStatus?: string
  /** Server-side: `user.subscriptionStatus === 'PAUSED'`. */
  paused?: boolean
  /** PR2: full vs read-only access. */
  access?: { mode?: string; reason?: string; trialEndsAt?: string | null }
}

// ── Demo-mode plan storage ────────────────────────────────────────

const DEMO_PLAN_KEY_NEW = 'bluaiq_demo_plan'
const DEMO_PLAN_KEY_LEGACY = 'constructai_demo_plan'

function readDemoPlan(): Plan {
  const stored = readLegacyMigratedItem(DEMO_PLAN_KEY_NEW, DEMO_PLAN_KEY_LEGACY)
  // In demo mode we tolerate any of the three plan keys.
  return normalizePlan(stored ?? 'STARTER')
}

function writeDemoPlan(plan: Plan): void {
  writeLegacyMigratedItem(DEMO_PLAN_KEY_NEW, DEMO_PLAN_KEY_LEGACY, plan)
}

function cycleDemoPlanValue(current: Plan): Plan {
  const i = VALID_PLANS.indexOf(current)
  return VALID_PLANS[(i + 1) % VALID_PLANS.length]
}

// ── Clerk-aware implementation ────────────────────────────────────
//
// Used when the bundle was built with VITE_CLERK_PUBLISHABLE_KEY. Calls
// `useAuth()` to pick up the bearer token and `useQuery()` to fetch
// `/api/subscription`. Demo overrides only apply in `isDemoMode()`.

function useSubscriptionPlanWithClerk(): SubscriptionPlanState {
  const { getToken, isSignedIn } = useAuth()

  const query = useQuery<SubscriptionApiResponse | null>({
    queryKey: ['subscription-plan'],
    enabled: !!isSignedIn,
    staleTime: 60_000,
    retry: 1,
    queryFn: async () => {
      const token = await getToken()
      const headers: Record<string, string> = {}
      if (token) headers['Authorization'] = `Bearer ${token}`
      const r = await apiFetch('/api/subscription', { headers })
      if (!r.ok) {
        // Treat any non-2xx (including 404 "user not found" during
        // the brief window before Clerk webhook provisions the row)
        // as a soft error — caller falls back to STARTER.
        throw new Error(`subscription_fetch_failed_${r.status}`)
      }
      return r.json()
    },
  })

  const demo = isDemoMode()
  const [demoPlanState, setDemoPlanState] = useState<Plan>(() =>
    demo ? readDemoPlan() : 'STARTER',
  )

  const cycleDemoPlan = useCallback(() => {
    if (!demo) return
    const next = cycleDemoPlanValue(demoPlanState)
    setDemoPlanState(next)
    writeDemoPlan(next)
  }, [demo, demoPlanState])

  const serverPlan: Plan | null = query.data ? normalizePlan(query.data.plan) : null
  const paused = !!query.data?.paused

  // Prefer the explicit `subscriptionStatus` field (PR #59+). If the
  // server response predates that field, fall back to the `paused`
  // boolean. Defaults to ACTIVE — we never invent PAST_DUE/CANCELLED.
  const serverStatus: SubscriptionStatus = query.data?.subscriptionStatus
    ? normalizeSubscriptionStatus(query.data.subscriptionStatus)
    : paused
      ? 'PAUSED'
      : 'ACTIVE'

  // SAFETY: in demo mode we honor the demo selection. In production we
  // honor the server. Prefer the server's `effectivePlan` (which
  // already downgrades CANCELLED/INCOMPLETE users to STARTER). Fall
  // back to `plan` if the server hasn't sent the new field, and to
  // STARTER otherwise. NEVER PRO.
  const serverEffectivePlan: Plan | null = query.data?.effectivePlan
    ? normalizePlan(query.data.effectivePlan)
    : null
  const effectivePlan: Plan = demo
    ? demoPlanState
    : serverEffectivePlan ?? serverPlan ?? 'STARTER'

  return {
    plan: serverPlan,
    subscriptionStatus: serverStatus,
    isLoading: query.isLoading,
    isError: query.isError,
    isDemo: demo,
    effectivePlan,
    cycleDemoPlan,
    // Demo mode never shows the read-only state.
    ...(demo ? { accessMode: 'full' as const, accessReason: null } : readAccessMode(query.data)),
  }
}

// ── Trial-mode (no Clerk) implementation ──────────────────────────
//
// Used when the bundle was built without VITE_CLERK_PUBLISHABLE_KEY.
// `<App />` is rendered without a `<ClerkProvider>` in this case
// (see src/main.tsx), so calling `useAuth()` would throw. We skip the
// fetch entirely. Demo plan still works through `isDemoMode()`.

function useSubscriptionPlanTrialMode(): SubscriptionPlanState {
  const demo = isDemoMode()
  const [demoPlanState, setDemoPlanState] = useState<Plan>(() =>
    demo ? readDemoPlan() : 'STARTER',
  )

  const cycleDemoPlan = useCallback(() => {
    if (!demo) return
    const next = cycleDemoPlanValue(demoPlanState)
    setDemoPlanState(next)
    writeDemoPlan(next)
  }, [demo, demoPlanState])

  // No Clerk → no /api/subscription fetch. We expose a stable
  // STARTER baseline for production-like trial bundles, and the demo
  // selection in demo mode.
  return {
    plan: null,
    subscriptionStatus: 'ACTIVE',
    isLoading: false,
    isError: false,
    isDemo: demo,
    effectivePlan: demo ? demoPlanState : 'STARTER',
    cycleDemoPlan,
  }
}

// ── Module-level Clerk-presence switch ────────────────────────────
//
// `import.meta.env.VITE_CLERK_PUBLISHABLE_KEY` is captured at build
// time, so this branch is decided once per bundle. Every call site
// sees a stable hook identity, complying with the rules of hooks.

// TASK-WEB-46: the optional chaining that used to sit here
// (`import.meta.env?.VITE_CLERK_PUBLISHABLE_KEY`) defeated Vite's static
// member replacement, so Vite inlined the WHOLE `VITE_*` map here too — a
// second copy of the leak fixed in `src/utils/api.ts`. Proven by a canary
// build: a `VITE_*` var referenced by no source file appeared at this exact
// site. The static member read below is what Vite can replace with a literal;
// the try/catch preserves importability under plain tsx, where
// `import.meta.env` is undefined. Behavior is unchanged — still a build-time,
// once-per-bundle boolean, so hook identity stays stable.
function readHasClerkFromViteEnv(): boolean {
  try {
    return !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
  } catch {
    return false
  }
}

const hasClerk = readHasClerkFromViteEnv()

export const useSubscriptionPlan: () => SubscriptionPlanState = hasClerk
  ? useSubscriptionPlanWithClerk
  : useSubscriptionPlanTrialMode
